import React, { useEffect } from 'react';
import { BrowserRouter, HashRouter, Routes, Route, useLocation, Navigate } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './redux/store';
import { preloadCriticalImages } from './utils/imagePreloader';
import './App.css';
import Header from './components/Header/Header';
import BannerSection from './components/BannerSection/BannerSection';
import FindYourRightCar from './components/FindYourRightCar/FindYourRightCar';
import LogosContainer from './components/LogosContainer/LogosContainer';
import GallerySection from './components/GallerySection/GallerySection';
import CarDetailWithInvoices from './components/CarDetailWithInvoices/CarDetailWithInvoices';
import BankLoans from './components/BankLoans/BankLoans';
import Footer from './components/Footer/Footer';
import NewCars from './pages/NewCars/NewCars';
import BusinessLoan from './pages/BusinessLoan/BusinessLoan';
import AboutUs from './pages/AboutUs/AboutUs';
import BlogList from './pages/Blogs/BlogList';
import BlogDetail from './pages/Blogs/BlogDetail';
import DashboardLayout from './components/dashboard/DashboardLayout';
import CRMPortal from './components/dashboard/crm/CRMPortal';
import CRMDashboard from './components/dashboard/crm/CRMDashboard';
import GlobalStateProvider from './components/GlobalStateProvider';
import CustomerTracking from './pages/CustomerTracking';
import ChatbotWidget from './components/ChatbotWidget/ChatbotWidget';
import TrustStatsCounter from './components/TrustStatsCounter/TrustStatsCounter';

const isElectron = /electron/i.test(navigator.userAgent);
const Router = isElectron ? HashRouter : BrowserRouter;

// ScrollToTop component that scrolls the page to the top when navigation occurs
const ScrollToTop: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

// App component - root component of the application
const App: React.FC = () => {
  useEffect(() => {
    // Preload critical images when the app starts
    preloadCriticalImages();
  }, []);

  return (
    <Provider store={store}>
      <GlobalStateProvider>
        <Router>
          <Routes>
            {isElectron && <Route path="/" element={<Navigate to="/crm" replace />} />}
            
            {/* Dedicated Standalone CRM Routes (No Quotation/Car Sidebar) */}
            <Route path="/crm" element={<CRMPortal />} />
            <Route path="/crm/app/*" element={<CRMDashboard />} />

            {/* Redirect legacy /dashboard/crm paths to clean /crm */}
            <Route path="/dashboard/crm" element={<Navigate to="/crm" replace />} />
            <Route path="/dashboard/crm/app/*" element={<Navigate to="/crm/app" replace />} />

            {/* Main Admin Dashboard */}
            <Route path="/dashboard/*" element={<DashboardLayout />} />
            <Route path="/track/:token" element={<CustomerTracking />} />
            <Route path="/*" element={<AppContent />} />
          </Routes>
        </Router>
      </GlobalStateProvider>
    </Provider>
  );
};

// This component handles which routes should show the homepage components
const AppContent = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isFinanceOffers = location.pathname === '/finance-offers';
  const isBusinessLoan = location.pathname === '/business-loan';
  
  return (
    <div className="App">
      <ScrollToTop />
      <Header />
      
      {/* Banner section - only on homepage */}
      {isHomePage && <BannerSection />}
      
      {/* Content area */}
      <div className="content-area">
        {/* Homepage specific components */}
        {isHomePage && (
          <>
            <FindYourRightCar />
            <TrustStatsCounter />
            <LogosContainer />
            <GallerySection />
          </>
        )}
        
        {/* Finance Offers page specific components */}
        {isFinanceOffers && (
          <>
            <BankLoans />
          </>
        )}
        
        {/* Business Loan page specific components */}
        {isBusinessLoan && (
          <>
            <BusinessLoan />
          </>
        )}
        
        {/* Page specific content */}
        <Routes>
          <Route path="/" element={null} />
          <Route path="/car/:carId" element={<CarDetailWithInvoices />} />
          <Route path="/new-cars" element={<NewCars />} />
          <Route path="/finance-offers" element={<></>} />
          { <Route path="/business-loan" element={null} /> }
          <Route path="/about-us" element={<AboutUs />} />
          <Route path="/blogs" element={<BlogList />} />
          <Route path="/blogs/:slug" element={<BlogDetail />} />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </div>
      
      <Footer />
      <ChatbotWidget />
    </div>
  );
};

export default App;
